/**
 * Sprint 26.0 — the Details footer (pane) / sidebar (page).
 *
 * Inline-edit `due_by` (date); `owner` is read-only. No recurrence field — a
 * task is one work item in the clean build. The pane renders a
 * horizontal footer; the page renders a boxed sidebar card with the same fields.
 */

import type { CSSProperties, ReactNode } from "react";
import type { Task } from "../../../lib/types";

export interface TaskDetailMetaProps {
  task: Task;
  ownerName: string;
  mount: "pane" | "page";
  onUpdate: (fields: { due_by?: string }) => void;
}

export function TaskDetailMeta({ task, ownerName, mount, onUpdate }: TaskDetailMetaProps) {
  const due = task.due_by ?? "";

  const fields = (
    <>
      <Field label="Due">
        <input
          type="date"
          value={due}
          onChange={(e) => onUpdate({ due_by: e.target.value })}
          style={inputStyle}
          data-testid="meta-due"
        />
      </Field>
      <Field label="Owner">
        <span style={readonlyValue}>{ownerName}</span>
      </Field>
    </>
  );

  if (mount === "page") {
    return (
      <div style={sidebarCard} data-testid="task-meta">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{fields}</div>
      </div>
    );
  }

  return (
    <div style={footer} data-testid="task-meta">
      {fields}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span style={fieldLabel}>{label}</span>
      {children}
    </div>
  );
}

const footer: CSSProperties = {
  display: "flex",
  gap: 18,
  padding: "10px 0",
  borderTop: "0.5px solid var(--color-border-tertiary)",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
  marginBottom: 14,
  flexWrap: "wrap",
};

const sidebarCard: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-lg)",
  padding: "14px 16px",
};

const fieldLabel: CSSProperties = { fontSize: 10, color: "var(--color-text-tertiary)" };

const readonlyValue: CSSProperties = { fontSize: 12, fontWeight: 500, color: "var(--color-text-primary)" };

const inputStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  background: "transparent",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-sm)",
  padding: "3px 6px",
  fontFamily: "var(--font-sans)",
  outline: "none",
};
